import { useParams } from "react-router";
import {
  faTrash,
  faArrowUpRightFromSquare
} from "@fortawesome/free-solid-svg-icons";
import { useForm, useWatch } from "react-hook-form";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  TableRow,
  FieldSet,
  CustomCard,
  PageHeading,
  BootstrapTable,
  TableDetailCell,
  LastHeaderColumn,
  FirstHeaderColumn,
  TableDetailNameCell
} from "../../common-styling";
import Loader from "../../loader/loader";
import {
  saveWebsiteFunnels,
  deleteWebsiteFunnel,
  fetchWebsiteFunnels,
  getCustomerActiveWebsiteTemplate
} from "../../../services/api-declaration";
import { useTabRoute } from "../../../hooks/useTabRoute";
import AssignFunnelsSection from "./assign-funnels-section";
import { showError, showSuccess } from "../../../services/toast-service";
import { useTranslation } from "react-i18next";

const getSortedFunnelIds = (funnels = []) =>
  funnels
    .map((funnel) => funnel?._id)
    .filter(Boolean)
    .sort();

const CustomerWebsiteFunnels = ({ isTabSelected }) => {
  const { t } = useTranslation();
  const { id: customerId } = useParams();
  const { tabRoute } = useTabRoute();
  const [website, setWebsite] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [removingFunnelId, setRemovingFunnelId] = useState(null);
  const [savedFunnelIds, setSavedFunnelIds] = useState([]);
  const { control, setValue } = useForm({
    defaultValues: { assignedFunnels: [] }
  });
  const watchedAssignedFunnels = useWatch({ control, name: "assignedFunnels" });
  const assignedFunnels = useMemo(
    () => watchedAssignedFunnels || [],
    [watchedAssignedFunnels]
  );
  const selectedFunnelIds = useMemo(
    () => getSortedFunnelIds(assignedFunnels),
    [assignedFunnels]
  );
  const hasFunnelsChanged = useMemo(
    () => selectedFunnelIds.join(",") !== savedFunnelIds.join(","),
    [savedFunnelIds, selectedFunnelIds]
  );

  const loadWebsiteFunnels = useCallback(async () => {
    if (!customerId || !isTabSelected) return;

    setIsLoading(true);
    try {
      const websiteResponse = await getCustomerActiveWebsiteTemplate({
        customerId,
        tabRoute
      });
      const activeWebsite = websiteResponse.data?.data;
      setWebsite(activeWebsite);

      if (!activeWebsite?._id) {
        setValue("assignedFunnels", []);
        setSavedFunnelIds([]);
        return;
      }

      const funnelsResponse = await fetchWebsiteFunnels(activeWebsite._id);
      const assignedFunnelsResponse = funnelsResponse.data?.data || [];
      setValue("assignedFunnels", assignedFunnelsResponse);
      setSavedFunnelIds(getSortedFunnelIds(assignedFunnelsResponse));
    } catch (error) {
      console.error("Error fetching website funnels:", error);
      showError(
        error?.response?.data?.message || "Error in getting website funnels!"
      );
    } finally {
      setIsLoading(false);
    }
  }, [customerId, isTabSelected, setValue, tabRoute]);

  useEffect(() => {
    loadWebsiteFunnels();
  }, [loadWebsiteFunnels]);

  const handleSaveFunnels = async () => {
    if (!website?._id) return;

    setIsSaving(true);
    try {
      const funnelIds = assignedFunnels
        .map((funnel) => funnel?._id)
        .filter(Boolean);
      const response = await saveWebsiteFunnels(website._id, {
        funnelIds,
        replaceExisting: true
      });
      const updatedFunnels = response.data?.data || [];
      setValue("assignedFunnels", updatedFunnels);
      setSavedFunnelIds(getSortedFunnelIds(updatedFunnels));
      showSuccess(response.data.message);
    } catch (error) {
      console.error("Error saving website funnels:", error);
      showError(error?.response?.data?.message || "Error in saving funnels!");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveFunnel = async (funnelId) => {
    if (!website?._id || !funnelId) return;

    setRemovingFunnelId(funnelId);
    try {
      const response = await deleteWebsiteFunnel(website._id, funnelId);
      setValue(
        "assignedFunnels",
        assignedFunnels.filter((funnel) => funnel?._id !== funnelId)
      );
      setSavedFunnelIds((prevFunnelIds) =>
        prevFunnelIds.filter((savedFunnelId) => savedFunnelId !== funnelId)
      );
      showSuccess(response.data.message);
    } catch (error) {
      console.error("Error removing website funnel:", error);
      showError(error?.response?.data?.message || "Error in removing funnel!");
    } finally {
      setRemovingFunnelId(null);
    }
  };

  if (isLoading) {
    return (
      <CustomCard>
        <Loader calcHeight={300} />
      </CustomCard>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between py-2">
        <PageHeading className="mt-0">{t("website.assignFunnels")}</PageHeading>
        <Button
          variant="dark"
          type="button"
          onClick={handleSaveFunnels}
          disabled={!website?._id || isSaving || !hasFunnelsChanged}
        >
          {isSaving && (
            <Spinner
              as="span"
              size="sm"
              role="status"
              className="me-2"
              animation="border"
              aria-hidden="true"
            />
          )}
          {t("common.save")}
        </Button>
      </div>
      <CustomCard className="mb-3">
        <FieldSet>
          {website?._id ? (
            <AssignFunnelsSection
              control={control}
              websiteId={website._id}
              isLoading={isSaving}
              showEmptyState={false}
            />
          ) : (
            <div className="text-secondary">{t("website.noWebsiteFound")}</div>
          )}
        </FieldSet>
      </CustomCard>
      <CustomCard>
        <FieldSet>
          <Row>
            <Col md={12}>
              {assignedFunnels.length ? (
                <BootstrapTable responsive>
                  <thead>
                    <tr>
                      <FirstHeaderColumn>
                        {t("common.selected")} Funnels
                      </FirstHeaderColumn>
                      <LastHeaderColumn className="text-center">
                        {t("common.action")}
                      </LastHeaderColumn>
                    </tr>
                  </thead>
                  <tbody>
                    {assignedFunnels.map((funnel) => (
                      <TableRow key={funnel._id}>
                        <TableDetailNameCell className="align-middle py-3">
                          {funnel?.general?.funnelName || funnel?.name}
                        </TableDetailNameCell>
                        <TableDetailCell className="text-center align-middle py-3">
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="rounded-circle"
                            onClick={() => handleRemoveFunnel(funnel._id)}
                            disabled={removingFunnelId === funnel._id}
                          >
                            {removingFunnelId === funnel._id ? (
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                              />
                            ) : (
                              <FontAwesomeIcon size="sm" icon={faTrash} />
                            )}
                          </Button>
                          <a
                            href={`/dashboard/funnels/${funnel._id}/form`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="rounded-circle ms-2"
                              disabled={!funnel._id}
                            >
                              <FontAwesomeIcon
                                size="sm"
                                icon={faArrowUpRightFromSquare}
                              />
                            </Button>
                          </a>
                        </TableDetailCell>
                      </TableRow>
                    ))}
                  </tbody>
                </BootstrapTable>
              ) : (
                <div className="text-secondary">
                  {t("website.noFunnelsAssigned")}
                </div>
              )}
            </Col>
          </Row>
        </FieldSet>
      </CustomCard>
    </div>
  );
};

export default CustomerWebsiteFunnels;
